import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { CurrentAccommodationSearchSessionStorage, DateUtils, doRequest } from '../../utils';

const SearchIdContext = createContext({
    searchId: null,
    updateSearchId: () => {},
});

const MIN_DAYS_INTERVAL = 1;
const MAX_ROOMS = 8;
const MIN_ADULTS = 1;
const MAX_ADULTS = 10;
const MAX_CHILDREN_PER_ROOM = 10;
const MAX_CHILD_AGE = 17;

const getRoomsFormatted = (rooms) =>
    rooms.map((room) => ({
        numAdults: room.adults,
        childrenAges: room.kids,
    }));

export const normalizeDate = (d) => {
    if (!d) {
        return null;
    }
    const dt = typeof d === 'string' ? DateUtils.parse(d, DateUtils.Format.ISO) : d;
    if (!dt || typeof dt.getTime !== 'function' || Number.isNaN(dt.getTime())) {
        return null;
    }
    return new Date(dt.getFullYear(), dt.getMonth(), dt.getDate());
};

export const hasValidKidAges = (kids) => {
    if (!Array.isArray(kids) || kids.length > MAX_CHILDREN_PER_ROOM) {
        return false;
    }
    return kids.every((age) => typeof age === 'number' && !Number.isNaN(age) && age >= 0 && age <= MAX_CHILD_AGE);
};

export const isValidRoom = (room) => {
    if (!room || typeof room.adults !== 'number' || Number.isNaN(room.adults) || room.adults < MIN_ADULTS || room.adults > MAX_ADULTS) {
        return false;
    }
    return hasValidKidAges(room.kids);
};

export const hasValidDestination = (destination) => Boolean(destination?.id && destination?.type);

export const hasValidDates = (dates) => {
    const checkInDate = normalizeDate(dates?.checkin);
    const checkOutDate = normalizeDate(dates?.checkout);
    if (!checkInDate || !checkOutDate) {
        return false;
    }

    const diffDays = Math.round((checkOutDate.getTime() - checkInDate.getTime()) / (1000 * 3600 * 24));
    return diffDays >= MIN_DAYS_INTERVAL && diffDays <= DateUtils.MAX_DAYS_PRIME_HOTEL;
};

export const hasValidRooms = (rooms) => {
    const roomsValue = rooms?.length > 0 ? rooms : [{ adults: 2, kids: [] }];
    if (roomsValue.length < 1 || roomsValue.length > MAX_ROOMS) {
        return false;
    }
    return roomsValue.every(isValidRoom);
};

export const isValidAccommodationSearchData = (searchData) => {
    if (!searchData) {
        return false;
    }
    const { dates, destination, rooms } = searchData;
    return hasValidDestination(destination) && hasValidDates(dates) && hasValidRooms(rooms);
};

export const getSearchBody = (searchData = CurrentAccommodationSearchSessionStorage.getAccommodationCurrentSearch()) => {
    if (!isValidAccommodationSearchData(searchData)) {
        return '';
    }
    const { dates, destination, rooms } = searchData;
    const checkIn = DateUtils.toString(normalizeDate(dates.checkin));
    const checkOut = DateUtils.toString(normalizeDate(dates.checkout));
    const roomsValue = rooms?.length > 0 ? rooms : [{ adults: 2, kids: [] }];
    return {
        buyPath: 7202, // HSA_HOME
        searchType: 'STANDALONE',
        membershipFee: false,
        geoAccommodation: {
            geoId: destination.id,
            geoType: destination.type,
        },
        checkInDate: checkIn,
        checkOutDate: checkOut,
        roomRequests: getRoomsFormatted(roomsValue),
        maxResults: 10,
        maxSize: 10,
        upsell: false,
    };
};

export const useSearchId = () => useContext(SearchIdContext);

export const SearchIdProvider = ({ children }) => {
    const [searchId, setSearchId] = useState(null);
    const requestCounterRef = useRef(0);

    const updateSearchId = useCallback(async() => {
        // Clear stale value immediately so an in-flight result is never used at submit time
        setSearchId(null);
        const requestId = ++requestCounterRef.current;
        try {
            const url = '/frontend-home/service/accommodation-search-async/v1/search';
            const body = getSearchBody();
            if (!body) {
                return;
            }
            const response = await doRequest(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json;charset=UTF-8',
                },
                body: JSON.stringify(body),
            }).then((response) => response.json());
            // Discard response if a newer request was fired while this one was in-flight
            if (requestId === requestCounterRef.current) {
                setSearchId(response.id);
            }
        } catch (error) {
            if (requestId === requestCounterRef.current) {
                setSearchId(null);
            }
        }
    }, []);

    return (
        <SearchIdContext.Provider value={{ searchId, updateSearchId }}>
            {children}
        </SearchIdContext.Provider>
    );
};

SearchIdProvider.propTypes = {
    children: PropTypes.node.isRequired,
};
